import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, Package, Calendar, Shield, Phone, Mail, ShoppingCart, MapPin, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductScan() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanForm, setScanForm] = useState({
    customer_name: '',
    customer_email: '',
    location: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    fetchProductInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrId]);

  const fetchProductInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/product/${qrId}`);
      setProduct(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScan = async (e) => {
    e.preventDefault();
    if (!scanForm.customer_name.trim()) {
      toast.error('Please enter your name to verify receipt');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/public/carton/${qrId}/scan`,
        scanForm
      );
      setScanResult(response.data);
      toast.success('Thank you! Your scan has been recorded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record scan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8FC043]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} data-testid="scan-go-home-btn">Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCarton = product?.type === 'carton';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#8FC043]/10 to-white">
      {/* Header */}
      <header className="bg-white border-b py-4">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-10 w-10 object-contain" />
            <span className="font-bold text-gray-900">Himan Investment</span>
          </div>
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" /> Verified
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Authenticity Badge */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-green-500 rounded-full p-2">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-green-800">Authentic Product</h3>
              <p className="text-sm text-green-700">This product is verified by Himan Investment & Company</p>
            </div>
          </div>
        </div>

        {/* Product Info Card */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">{product.product_name}</CardTitle>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant="outline">Batch: {product.batch_number}</Badge>
              {isCarton && (
                <Badge className="bg-[#EA5829]/10 text-[#EA5829] font-mono">
                  Carton: {product.carton_id}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Production Date</p>
                <p className="font-semibold flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(product.production_date).toLocaleDateString()}
                </p>
              </div>
              {product.expiry_date && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Expiry Date</p>
                  <p className="font-semibold flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(product.expiry_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-2">Product Features</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Premium quality dustless chalk
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Safe for health - non-toxic
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Smooth and easy to erase
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Made in Tanzania
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Scan / Verify Receipt Form (only for unique cartons) */}
        {isCarton && !scanResult && (
          <Card className="mb-6 border-[#8FC043]/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User size={18} className="text-[#8FC043]" />
                Verify Your Receipt
              </CardTitle>
              <p className="text-sm text-gray-600">
                Confirm you received this carton. We'll record your scan to track this unique box.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitScan} className="space-y-3" data-testid="scan-verify-form">
                <div className="space-y-1">
                  <Label htmlFor="customer_name">Your Name *</Label>
                  <Input
                    id="customer_name"
                    data-testid="scan-name-input"
                    value={scanForm.customer_name}
                    onChange={(e) => setScanForm({ ...scanForm, customer_name: e.target.value })}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_email">Email (optional)</Label>
                  <Input
                    id="customer_email"
                    data-testid="scan-email-input"
                    type="email"
                    value={scanForm.customer_email}
                    onChange={(e) => setScanForm({ ...scanForm, customer_email: e.target.value })}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="location">Location / Region (optional)</Label>
                  <Input
                    id="location"
                    data-testid="scan-location-input"
                    value={scanForm.location}
                    onChange={(e) => setScanForm({ ...scanForm, location: e.target.value })}
                    placeholder="e.g. Dar es Salaam"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#8FC043] hover:bg-[#7aa936]"
                  data-testid="scan-submit-btn"
                >
                  {submitting ? 'Recording...' : 'Confirm & Record Scan'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scan success state */}
        {scanResult && (
          <Card className="mb-6 border-green-300 bg-green-50" data-testid="scan-success-card">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-green-800 mb-1">Scan Recorded!</h3>
              <p className="text-sm text-green-700 mb-3">
                Thank you. This carton is now marked as{' '}
                <b>{scanResult.status === 'delivered' ? 'Delivered' : 'Sold via Scan'}</b>.
              </p>
              {scanResult.stock_deducted && (
                <p className="text-xs text-green-600 mb-3">Stock has been updated automatically.</p>
              )}
              <p className="text-xs text-gray-600">
                Carton: <code className="bg-white px-2 py-0.5 rounded">{scanResult.carton.id}</code>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Order Button */}
        <Button
          className="w-full bg-[#8FC043] hover:bg-[#7aa936] py-6 text-lg"
          onClick={() => navigate('/customer/products')}
          data-testid="scan-order-more-btn"
        >
          <ShoppingCart className="mr-2" />
          Order More Products
        </Button>

        {/* Company Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-700">{product.company}</p>
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-gray-500" />
              <a href={`mailto:${product.contact}`} className="text-[#8FC043] hover:underline">
                {product.contact}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={14} className="text-gray-500" />
              <a href="tel:+255767408555" className="text-[#8FC043] hover:underline">
                +255 767 408 555
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={14} className="text-gray-500" />
              <span>Tanzania</span>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          Scan this QR code to verify product authenticity<br />
          © {new Date().getFullYear()} Himan Investment & Company
        </p>
      </main>
    </div>
  );
}
